import React from 'react';
import './About.css'; // Reuse About page styling

const CommunityGuidelines = () => {
  return (
    <div className="about-page">
      <div className="about-container">
        <div className="about-content">
          <h1 className="about-title">Community Guidelines</h1>

          {/* Why Invite-Only Section */}
          <section className="about-section">
            <h2>Why All Rooms Are Invite-Only</h2>
            <p className="about-subtitle">
              We made a deliberate design choice: <strong>no public matchmaking</strong>. This keeps
              the game fun for friend groups without us becoming content police.
            </p>

            <div className="feature-grid">
              <div className="feature-card">
                <div className="feature-icon">🛡️</div>
                <h3>Less Moderation Needed</h3>
                <ul>
                  <li>Friends playing together rarely report each other</li>
                  <li>Shared context - everyone knows it's dark humor</li>
                  <li>Self-regulating groups manage their own boundaries</li>
                </ul>
              </div>

              <div className="feature-card">
                <div className="feature-icon">⚖️</div>
                <h3>Reduced Legal Risk</h3>
                <ul>
                  <li>Implied consent when you invite someone to play</li>
                  <li>Not facilitating stranger-to-stranger harassment</li>
                  <li>Harder to claim "I was victimized by random internet people"</li>
                </ul>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🎮</div>
                <h3>Industry Precedent</h3>
                <ul>
                  <li>
                    <strong>Jackbox Games:</strong> All room codes, no public matchmaking
                  </li>
                  <li>
                    <strong>Among Us:</strong> Started with private lobbies only
                  </li>
                  <li>
                    <strong>Tabletop Simulator:</strong> Mostly friend groups
                  </li>
                </ul>
              </div>

              <div className="feature-card">
                <div className="feature-icon">✏️</div>
                <h3>Write-Ins Context Matters</h3>
                <p>
                  <strong>Among friends:</strong> "Shoot up [friend's workplace]" is obviously a
                  joke. Everyone laughs.
                </p>
                <p>
                  <strong>With strangers:</strong> Same text → potential FBI call.
                </p>
                <p>Context is everything. Invite-only preserves context.</p>
              </div>
            </div>
          </section>

          {/* Our Approach Section */}
          <section className="about-section">
            <h2>Our Approach: Transparency Over Promises</h2>
            <p>
              We're a small, pre-revenue project. We <strong>cannot</strong> (and will not) promise
              active content moderation. Here's what we do instead:
            </p>

            <div className="principles-list">
              <div className="principle">
                <h3>✅ What We Do</h3>
                <ul>
                  <li>
                    <strong>Invite-only design:</strong> You control who joins your game
                  </li>
                  <li>
                    <strong>Card attribution:</strong> Write-ins clearly labeled so you know the
                    source
                  </li>
                  <li>
                    <strong>Rate limiting:</strong> Prevent spam and abuse
                  </li>
                  <li>
                    <strong>Ephemeral data:</strong> Write-ins deleted when games end (we don't
                    store them)
                  </li>
                  <li>
                    <strong>Clear disclaimers:</strong> You're responsible for your content
                  </li>
                  <li>
                    <strong>Host controls:</strong> Remove disruptive players from your room
                  </li>
                  <li>
                    <strong>Experimental safeguards:</strong> We're testing automated approaches to
                    prevent egregious content while preserving the game's spirit
                  </li>
                </ul>
              </div>

              <div className="principle">
                <h3>❌ What We Don't Do</h3>
                <ul>
                  <li>
                    <strong>No active moderation:</strong> We don't review write-in content
                  </li>
                  <li>
                    <strong>No reporting system:</strong> Manage your own room
                  </li>
                  <li>
                    <strong>No ban lists:</strong> Invite-only means no strangers to ban
                  </li>
                  <li>
                    <strong>No content filtering:</strong> It's a deliberately offensive party game
                    - you know what you signed up for
                  </li>
                  <li>
                    <strong>No data retention:</strong> Write-ins and custom decks are deleted when
                    games end
                  </li>
                  <li>
                    <strong>No text or voice chat:</strong> This keeps things simple and reduces
                    moderation needs. Use Discord, Zoom, or whatever you prefer for communication.
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Player Responsibilities Section */}
          <section className="about-section">
            <h2>Your Responsibilities</h2>
            <p>When you create or join a game, you agree to:</p>

            <div className="responsibility-card">
              <h3>If You're the Host</h3>
              <ul>
                <li>
                  <strong>Invite responsibly:</strong> Only share room codes with people you trust
                </li>
                <li>
                  <strong>Manage your room:</strong> Remove disruptive players if needed
                </li>
                <li>
                  <strong>Set expectations:</strong> Let players know your group's boundaries
                </li>
              </ul>
            </div>

            <div className="responsibility-card">
              <h3>If You're Creating Write-Ins</h3>
              <ul>
                <li>
                  <strong>You own your content:</strong> You're solely responsible for what you
                  write
                </li>
                <li>
                  <strong>Don't include personal info:</strong> No real names, addresses, phone
                  numbers of others
                </li>
                <li>
                  <strong>Know your audience:</strong> Jokes that work with your friends may not
                  work with strangers
                </li>
                <li>
                  <strong>Assume screenshots:</strong> Anything you write could be screenshot and
                  shared
                </li>
                <li>
                  <strong>Don't violate laws:</strong> No credible threats, illegal content, or
                  harassment
                </li>
              </ul>
            </div>

            <div className="responsibility-card">
              <h3>If You're Just Playing</h3>
              <ul>
                <li>
                  <strong>You chose to join:</strong> You accepted the invite - you know what this
                  game is
                </li>
                <li>
                  <strong>Talk to the host:</strong> If someone's making you uncomfortable, ask the
                  host to remove them
                </li>
                <li>
                  <strong>Leave if needed:</strong> You can exit the game at any time
                </li>
              </ul>
            </div>
          </section>

          {/* What Happens If Section */}
          <section className="about-section">
            <h2>What Happens If Something Goes Wrong?</h2>

            <div className="qa-section">
              <div className="qa-item">
                <h3>Q: What if someone creates an offensive write-in?</h3>
                <p>
                  <strong>A:</strong> The host can remove that player from the room. Since all rooms
                  are invite-only, this should be rare (you invited them).
                </p>
              </div>

              <div className="qa-item">
                <h3>Q: What if someone creates a credible threat?</h3>
                <p>
                  <strong>A:</strong> Contact local authorities directly. We don't store write-in
                  content after games end, so we can't provide historical data. If there's an active
                  game, we may assist law enforcement upon legal request.
                </p>
              </div>

              <div className="qa-item">
                <h3>Q: Can I report inappropriate content?</h3>
                <p>
                  <strong>A:</strong> We don't have a reporting system. Since rooms are invite-only,
                  you control who joins. Remove disruptive players or leave the game.
                </p>
              </div>

              <div className="qa-item">
                <h3>Q: Do you moderate write-in cards?</h3>
                <p>
                  <strong>A:</strong> No. We're a small project and can't provide active moderation.
                  This is an adult party game - players are responsible for their own content.
                </p>
              </div>

              <div className="qa-item">
                <h3>Q: What data do you keep?</h3>
                <p>
                  <strong>A:</strong> Write-in cards and custom deck content are deleted when games
                  end. We don't retain a historical record. See our{' '}
                  <a href="/privacy" className="link">
                    Privacy Policy
                  </a>{' '}
                  for details.
                </p>
              </div>
            </div>
          </section>

          {/* Bottom Line Section */}
          <section className="about-section highlight-section">
            <h2>The Bottom Line</h2>
            <p className="large-text">
              <strong>
                This is a deliberately offensive party game. We're not content police.
              </strong>
            </p>
            <p>
              By keeping rooms invite-only and putting responsibility on players, we avoid the
              moderation nightmare that killed other clones. We're transparent about what we do (and
              don't) offer.
            </p>
            <p>
              If you want a heavily moderated experience, this isn't it. If you want to play with
              your friends without algorithmic nannying, welcome.
            </p>
          </section>

          {/* Legal Links */}
          <section className="about-section">
            <h2>Legal Stuff</h2>
            <p>For the fine print:</p>
            <ul>
              <li>
                <a href="/terms" className="link">
                  Terms of Service
                </a>{' '}
                - Your legal responsibilities
              </li>
              <li>
                <a href="/privacy" className="link">
                  Privacy Policy
                </a>{' '}
                - How we handle (or don't handle) your data
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CommunityGuidelines;
